import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { SupportingDocumentation } from "./supportingDocumentation";
import { TransactionalFields } from "./transactionalFields";
import { createMutationObserver } from "./utils";
import formConfig from "../docs/formDefault.json";

export class CustomSubmitClaim extends SubmitClaim {
    /**
     * Custom Submit Claim class.
     *
     * @param {Object} options - The options object.
     * @param {string} options.appClaimForm - The application claim form.
     * @param {string} options.userLanguage - The user language.
     * @param {Function?} options.validateForm - The validate form function.
     * @param {Function?} options.customInitClaim - The custom initialization function.
     * @param {Function?} options.customProcessClaim - The custom processing function.
     * @param {Function?} options.customLoadMember - The custom loading function.
     * @param {Function?} options.customDstProcess - The custom DST processing function.
     * @param {Function?} options.customAddClaimFile - The custom add claim file function.
     * @param {ErrorHandler?} options.errorHandler - The error handler.
     * @param {Function?} options.onTransactionRowAdded - The callback function.
     * @param {Function?} options.onTransactionRowRemoved - The callback function.
     */
    constructor({
        appClaimForm,
        userLanguage,
        validateForm = null,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        customAddClaimFile = null,
        errorHandler = null,
        onTransactionRowAdded = null,
        onTransactionRowRemoved = null,
    }) {
        super(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
        this.customInitClaim = customInitClaim
            ? customInitClaim.bind(null, this)
            : handleInitClaim.bind(this, validateForm);
        this.customProcessClaim = customProcessClaim
            ? customProcessClaim.bind(null, this)
            : handleProcessClaim.bind(this, validateForm);
        this.customAddClaimFile = customAddClaimFile
            ? customAddClaimFile.bind(null, this)
            : handleAddClaimFile.bind(this, validateForm);
        this.onTransactionRowAdded = onTransactionRowAdded;
        this.onTransactionRowRemoved = onTransactionRowRemoved;
    }

    form() {
        const form = formConfig.claim_form[this.lang];
        this.init_claim(form);
    }

    /**
     * Creates a claim form element with the given parameters and additional parameters.
     *
     * @param {Object} params - The form parameters.
     * @param {Object} additionalParams - Additional parameters for the form.
     * @returns {HTMLFormElement} - The created form element.
     */
    claim_form(params, additionalParams) {
        const form = super.claim_form(params, additionalParams);
        this.formElement = form;
        this.observer = createMutationObserver({
            target: form,
            handleMutations: this.handleMutations.bind(this),
        });
        return form;
    }

    /**
     * Creates a fieldset for the claim form based on the provided parameters.
     * @param {Object} params - The parameters for the claim form.
     * @param {Array} params.claim_fields - The fields for the claim form.
     * @param {Object} params.form_options - The options for the claim form.
     * @returns {HTMLElement} The fieldset element for the claim form.
     */
    claim_fieldset(params) {
        const claimFields = super.claim_fieldset(params);
        // set className "row" to the third div child of claimFields
        claimFields.children[2].classList.add("row");
        return claimFields;
    }

    claim_documentation(params) {
        const container = super.claim_documentation(params);
        this.supportingDocumentation = new SupportingDocumentation(container);
        return container;
    }

    claim_transactions(params) {
        const container = super.claim_transactions(params);
        this.transactionalFields = new TransactionalFields({
            container: container.querySelector("table.transactions"),
            onRowAdded: this.onTransactionRowAdded,
            onRowRemoved: this.onTransactionRowRemoved,
        });
        return container;
    }

    add_claim_file(e) {
        super.add_claim_file(e);
        if (this.customAddClaimFile) {
            this.customAddClaimFile(e);
        }
    }

    init_claim_inputs() {
        super.init_claim_inputs();

        // update claim field detection to allow embedding transactional fields within the claim fields container
        const claimInputsFields =
            "#claim-fields input:not(table.transactions input), #claim-fields textarea:not(table.transactions textarea), #claim-fields select:not(table.transactions select)";
        const claimInputsFieldsPayment =
            "#payment_method, #payment_token, #payment_email, #payment_phone";
        const claimInputsTransactions =
            "table.transactions input, table.transactions textarea, table.transactions select";

        this.claim.inputs.fields = Array.from(
            document.querySelectorAll(
                claimInputsFields + ", " + claimInputsFieldsPayment
            )
        );

        this.claim.inputs.transactions = Array.from(
            document.querySelectorAll(claimInputsTransactions)
        );
    }

    /**
     * Gets a claim field by name.
     *
     * @param {string} name - The name of the field.
     * @returns {HTMLInputElement} - The field element.
     */
    getFormInput(name) {
        const input = this.formElement.elements.namedItem(name);
        if (!input) {
            throw new Error(`Form input with name "${name}" not found`);
        }

        if (input instanceof RadioNodeList) {
            throw new Error(
                `Form input with name "${name}" is a RadioNodeList`
            );
        }

        return input;
    }

    /**
     * Gets a form radio input by name.
     *
     * @param {string} name - The name of the field.
     * @returns {RadioNodeList} - The radio node list.
     */
    getFormRadioInput(name) {
        const input = this.formElement.elements.namedItem(name);
        if (!(input instanceof RadioNodeList)) {
            throw new Error(
                `Form input with name "${name}" is not a RadioNodeList`
            );
        }

        return input;
    }

    /**
     * Cleans up the form.
     */
    cleanup() {
        this.observer.disconnect();
        this.transactionalFields?.cleanup();
    }

    /**
     * Handles the mutations.
     *
     * @param {MutationRecord[]} mutations - The mutations.
     */
    handleMutations(mutations) {
        mutations.forEach((mutation) => {
            if (
                mutation.type === "childList" &&
                mutation.removedNodes.item(0)?.id === this.formElement.id
            ) {
                this.cleanup();
            }
        });
    }

    populate_claim_form(form, container, claim, deadline) {
        const isClaimant = claim.claim_id != null;
        const isClaimEditable = isClaimant
            ? claim.class_member?.is_claim_editable
            : claim.is_claim_editable;
        const isClaimDeficient = isClaimant
            ? claim.class_member?.is_claim_deficient
            : claim.is_claim_deficient;
        const deficiencyReasons = isClaimant
            ? claim.class_member?.deficiency_reasons
            : claim.deficiency_reasons;
        const doesClaimHaveTransactions =
            isClaimant && claim.claim_transactions.length > 0;
        const doesClaimHaveFiles = isClaimant && claim.claim_files.length > 0;
        if (isClaimEditable !== 1) {
            this.errorHandler.display({
                status: 403,
                error: "This claim is no longer editable. Please contact the settlement administrator for assistance.",
            });
            return;
        }

        if (isClaimEditable !== 1) {
            this.errorHandler.display({
                status: 403,
                error: "This claim is not eligible for edits at this time, please contact the settlement administrator for assistance.",
            });
            return;
        }

        this.claim.is_deficient = isClaimDeficient === 1;
        this.claim.deficiency_reasons = deficiencyReasons?.split(",") ?? [];
        this.claim.has_transactions = doesClaimHaveTransactions;
        this.claim.has_files = doesClaimHaveFiles;
        super.populate_claim_form(form, container, claim, deadline);
    }
}

/**
 * Initialize the claim.
 *
 * @this {CustomSubmitClaim}
 * @param {Function} validateForm - The validate form function.
 * @returns {Object} The claim object.
 */
export function handleInitClaim(validateForm) {
    const { formElement } = this;
    const handleEvent = (event) => validateForm({ event, ctx: this });

    formElement.addEventListener("input", handleEvent);

    // initial validation
    validateForm({ ctx: this, event: "init_claim" });

    // cleanup
    window.addEventListener("beforeunload", () => {
        formElement.removeEventListener("input", handleEvent);
    });

    return this.claim;
}

/**
 * Processes the claim.
 *
 * @this {CustomSubmitClaim}
 * @param {Function} validateForm - The validate form function.
 * @returns {Object} The processed claim.
 */
export function handleProcessClaim(validateForm) {
    validateForm({ ctx: this, event: "process_claim" });
    return this.claim;
}

/**
 * Adds a claim file.
 *
 * @this {CustomSubmitClaim}
 * @param {Function} validateForm - The validate form function.
 * @returns {void}
 */
function handleAddClaimFile(validateForm) {
    validateForm({ ctx: this, event: "add_claim_file" });
}
